import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Antenna,
  CircleDollarSign,
  Gauge,
  Settings2,
  TableProperties,
} from "lucide-react";

import { api, ApiError } from "@/api/client";
import type {
  CapacityOracleSettings,
  CapacitySignal,
  ProbeResult,
  ProviderOut,
} from "@/api/types";
import { MetricTile } from "@/components/MetricTile";
import { Pill, type PillTone } from "@/components/Pill";
import { Section } from "@/components/Section";

/**
 * Capacity page: oracle settings up top (they are the operator's
 * levers), then ONE flat, filterable list of every capacity signal.
 * Each row states HOW the value was estimated (source + confidence +
 * age): the oracle is advisory, and an operator can only trust or
 * overrule a claim whose provenance they can see.
 */

// --- the shared verdict scale -------------------------------------------
//
// The server computes `verdict` / `trust` with the same function the
// placement scorer folds into its beliefs, so this page cannot tell the
// operator something the scheduler disagrees with. The UI's only job is
// to make the two questions unmissable:
//
//   verdict — is this cell GOOD or BAD to place in?
//   trust   — should I believe it?
//
// Nothing here re-derives meaning from a signal's raw `value` bag; that
// is what made "capacity available" appear for GCP rows where nothing
// had been checked.

const VERDICT_LABEL: Record<string, string> = {
  available: "AVAILABLE",
  tight: "TIGHT",
  none: "NONE",
  blocked: "BLOCKED",
  offered: "NOT CHECKED",
  unknown: "UNKNOWN",
};

const VERDICT_TONE: Record<string, PillTone> = {
  available: "ok",
  tight: "warn",
  none: "danger",
  blocked: "danger",
  offered: "neutral",
  unknown: "neutral",
};

const VERDICT_INFO: Record<string, string> = {
  available: "Good — there is evidence of stock here right now.",
  tight: "Scarce — some evidence, but weak. Expect failures under load.",
  none: "Bad — evidence says there is no stock here.",
  blocked:
    "Bad — we are not ALLOWED to run here (quota), so stock is irrelevant. " +
    "Unlike a stock-out this never clears on its own: someone must raise the quota.",
  offered:
    "Neutral — the shape is SOLD in this location, but nothing has checked " +
    "whether any is in stock. Not a reason to place here, and not a reason not to.",
  unknown: "Neutral — no usable evidence either way.",
};

const TRUST_LABEL: Record<string, string> = {
  measured: "MEASURED",
  provider: "PROVIDER",
  estimate: "ESTIMATE",
  inferred: "INFERRED",
  stale: "STALE",
  unknown: "?",
};

const TRUST_TONE: Record<string, PillTone> = {
  measured: "brand",
  provider: "info",
  estimate: "neutral",
  inferred: "neutral",
  stale: "warn",
  unknown: "neutral",
};

function ageLabel(iso: string): string {
  const sec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 90) return `${Math.round(sec)}s ago`;
  if (sec < 5400) return `${Math.round(sec / 60)}m ago`;
  return `${(sec / 3600).toFixed(1)}h ago`;
}

/** Plain-language provenance, per spec §7.1. */
function methodLabel(sig: CapacitySignal): string {
  if (sig.source === "provider_api") {
    return sig.kind === "free_count"
      ? "cluster realtime count"
      : "provider stock API";
  }
  if (sig.source === "probe") return "active probe (real allocation)";
  const outcome = String(sig.value["outcome"] ?? "");
  return outcome === "ok"
    ? "learned from a successful launch"
    : `learned from a failed launch (${outcome || "error"})`;
}

const POLL_PRESETS = [
  { label: "every 5 min", value: 300 },
  { label: "every 10 min", value: 600 },
  { label: "every 15 min", value: 900 },
  { label: "every 30 min", value: 1800 },
  { label: "every 60 min", value: 3600 },
  { label: "every 120 min", value: 7200 },
];

// Periodic active-probe cadence. "" = disabled (probe_interval_seconds
// null server-side); values are minutes.
const PROBE_INTERVAL_PRESETS = [
  { label: "disabled", value: "" },
  { label: "every 5 min", value: "5" },
  { label: "every 10 min", value: "10" },
  { label: "every 15 min", value: "15" },
  { label: "every 30 min", value: "30" },
  { label: "every 60 min", value: "60" },
  { label: "every 120 min", value: "120" },
];

export function GlobalCapacity() {
  const qc = useQueryClient();
  const signalsQ = useQuery({
    queryKey: ["capacity-signals"],
    queryFn: () => api.get<CapacitySignal[]>("/capacity/signals"),
    refetchInterval: 60_000,
  });
  const settingsQ = useQuery({
    queryKey: ["capacity-settings"],
    queryFn: () => api.get<CapacityOracleSettings>("/capacity/settings"),
  });
  // Enabled placement providers ≠ providers with signals: AWS/GCP have
  // no stock API for on-demand (LEARNED-only, spec §2) so they appear
  // in the map only after a real launch — but they ARE candidates for
  // placement as soon as sky check passes. Show both counts so the
  // difference is visible instead of confusing.
  const providersQ = useQuery({
    // Same key/endpoint as useProvidersQuery() in api/local/providers.ts so
    // React Query dedupes with the Providers page instead of double-fetching.
    queryKey: ["providers"],
    queryFn: () => api.get<ProviderOut[]>("/providers"),
    staleTime: 30_000,
  });
  const enabledProviders = useMemo(
    () =>
      [
        ...new Set(
          (providersQ.data ?? [])
            .filter((p) => p.scope === "global" && p.enabled)
            .map((p) => p.kind),
        ),
      ].sort(),
    [providersQ.data],
  );

  const patchSettings = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      api.patch<CapacityOracleSettings>("/capacity/settings", { values }),
    onSuccess: (data) => qc.setQueryData(["capacity-settings"], data),
  });

  const signals = signalsQ.data ?? [];
  const fresh = signals.filter((s) => !s.expired);
  const providers = useMemo(
    () => [...new Set(signals.map((s) => s.provider))].sort(),
    [signals],
  );
  const kinds = useMemo(
    () => [...new Set(signals.map((s) => s.kind))].sort(),
    [signals],
  );

  // --- settings form ------------------------------------------------
  const eff = settingsQ.data?.effective ?? {};
  const [pollDraft, setPollDraft] = useState<number | null>(null);
  const [probeIntervalDraft, setProbeIntervalDraft] = useState<string | null>(
    null,
  );
  const [budgetDraft, setBudgetDraft] = useState<string | null>(null);

  const pollValue = pollDraft ?? Number(eff["poll_interval_seconds"] ?? 600);
  const probeIntervalMin =
    probeIntervalDraft ??
    (eff["probe_interval_seconds"] != null
      ? String(Number(eff["probe_interval_seconds"]) / 60)
      : "");
  const budgetValue =
    budgetDraft ?? String(eff["probe_budget_usd_per_day"] ?? "10");
  const dirty =
    pollDraft !== null || probeIntervalDraft !== null || budgetDraft !== null;


  const saveSettings = () => {
    patchSettings.mutate({
      poll_interval_seconds: pollValue,
      probe_budget_usd_per_day: Number(budgetValue) || 0,
      probe_interval_seconds:
        probeIntervalMin === "" ? null : Number(probeIntervalMin) * 60,
    });
    setPollDraft(null);
    setProbeIntervalDraft(null);
    setBudgetDraft(null);
  };

  const stats = settingsQ.data?.probe_stats ?? {};
  const fmtUsd = (v: unknown) =>
    typeof v === "number"
      ? v < 0.01
        ? `$${v.toFixed(4)}`
        : `$${v.toFixed(2)}`
      : "—";
  const probeSpendHint =
    (stats["probe_count_today"] ?? 0) > 0
      ? `today: ${fmtUsd(stats["spend_today_usd"])} (${stats["probe_count_today"]} probes · avg ${fmtUsd(stats["avg_probe_cost_usd"])}/probe` +
        ((stats["last_sweep_count"] ?? 0) > 0
          ? ` · last sweep ${fmtUsd(stats["last_sweep_cost_usd"])} for ${stats["last_sweep_count"]}`
          : "") +
        ")"
      : "no probes today — spend rolls into the Cost totals";

  // --- list filters -------------------------------------------------
  const [providerFilter, setProviderFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [gpuFilter, setGpuFilter] = useState("");
  const [hideExpired, setHideExpired] = useState(true);
  const [inStockOnly, setInStockOnly] = useState(false);

  const rows = useMemo(() => {
    const inStock = (s: CapacitySignal) => {
      if (s.kind === "stock_level")
        return !["none", ""].includes(String(s.value["level"] ?? "none"));
      if (s.kind === "free_count") return Number(s.value["free"] ?? 0) > 0;
      if (s.kind === "launch_outcome")
        return String(s.value["outcome"]) === "ok";
      return true;
    };
    return signals
      .filter((s) => providerFilter === "all" || s.provider === providerFilter)
      .filter((s) => kindFilter === "all" || s.kind === kindFilter)
      .filter(
        (s) => statusFilter === "all" || (s.verdict ?? "unknown") === statusFilter,
      )
      .filter(
        (s) =>
          !gpuFilter ||
          s.gpu.toLowerCase().includes(gpuFilter.trim().toLowerCase()),
      )
      .filter((s) => !hideExpired || !s.expired)
      .filter((s) => !inStockOnly || inStock(s))
      .sort(
        (a, b) =>
          Number(a.expired) - Number(b.expired) ||
          a.provider.localeCompare(b.provider) ||
          a.gpu.localeCompare(b.gpu) ||
          a.zone.localeCompare(b.zone) ||
          a.region.localeCompare(b.region),
      );
  }, [
    signals,
    providerFilter,
    kindFilter,
    statusFilter,
    gpuFilter,
    hideExpired,
    inStockOnly,
  ]);

  const statusOptions = useMemo(
    () => [...new Set(signals.map((s) => s.verdict ?? "unknown"))].sort(),
    [signals],
  );

  return (
    <>
      <div className="metric-grid" style={{ marginBottom: 12 }}>
        <MetricTile
          icon={<Antenna />}
          label="Fresh signals"
          value={fresh.length}
          unit={`/ ${signals.length}`}
          tone="brand"
          hint="observations within their TTL"
        />
        <MetricTile
          icon={<Gauge />}
          label="Providers"
          value={`${providers.length} / ${enabledProviders.length || "?"}`}
          unit="with signals / enabled"
          hint={
            enabledProviders.length
              ? `enabled: ${enabledProviders.join(", ")}`
              : providers.join(", ") || "no observations yet"
          }
        />
        <MetricTile
          icon={<Settings2 />}
          label="Poll interval"
          value={`${Math.round(Number(eff["poll_interval_seconds"] ?? 600) / 60)} min`}
          hint={
            eff["probe_interval_seconds"]
              ? `probe sweep every ${Math.round(
                  Number(eff["probe_interval_seconds"]) / 60,
                )} min`
              : "probe sweep off"
          }
        />
        <MetricTile
          icon={<CircleDollarSign />}
          label="Probe budget"
          value={`$${Number(eff["probe_budget_usd_per_day"] ?? 1)}`}
          unit="/ day / provider"
          hint={probeSpendHint}
        />
      </div>

      <Section title="Oracle settings" icon={<Settings2 />}>
        <div className="form-grid">
          <label>poll interval</label>
          <div className="field">
            <select
              value={pollValue}
              onChange={(e) => setPollDraft(Number(e.target.value))}
            >
              {POLL_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
              {!POLL_PRESETS.some((p) => p.value === pollValue) && (
                <option value={pollValue}>
                  every {Math.round(pollValue / 60)} min
                </option>
              )}
            </select>
            <div className="field-hint">
              How often provider stock APIs are polled. Applies from the next
              tick — no restart needed.
            </div>
          </div>

          <label>probe interval</label>
          <div className="field">
            <select
              value={
                PROBE_INTERVAL_PRESETS.some((p) => p.value === probeIntervalMin)
                  ? probeIntervalMin
                  : ""
              }
              onChange={(e) => setProbeIntervalDraft(e.target.value)}
            >
              {PROBE_INTERVAL_PRESETS.map((p) => (
                <option key={p.label} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
            <div className="field-hint">
              Cadence of the periodic probe sweep over the pools' placement
              candidates. Independent from the API poll interval above.
            </div>
          </div>

          <label>probe budget (USD/day)</label>
          <div className="field">
            <input
              type="number"
              min={0}
              step={0.5}
              value={budgetValue}
              onChange={(e) => setBudgetDraft(e.target.value)}
            />
            <div className="field-hint">
              Per-provider daily cap. Probe spend is billed for real and is
              included in the Cost page totals.
            </div>
          </div>

          <label />
          <div className="field">
            <button
              className="btn btn--primary"
              onClick={saveSettings}
              disabled={!dirty || patchSettings.isPending}
            >
              {patchSettings.isPending ? "Saving…" : "Save settings"}
            </button>
            {patchSettings.isError && (
              <div className="field-hint" style={{ color: "var(--danger)" }}>
                Save failed: {String(patchSettings.error)}
              </div>
            )}
          </div>
        </div>
      </Section>

      <Section
        title="Capacity signals"
        icon={<TableProperties />}
        count={rows.length}
      >
        <div className="toolbar" style={{ marginBottom: 10 }}>
          <select
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
          >
            <option value="all">all providers</option>
            {providers.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
          >
            <option value="all">all signal kinds</option>
            {kinds.map((k) => (
              <option key={k} value={k} title={VERDICT_INFO[k]}>
                {k}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            title="Filter by the STATUS column's value"
          >
            <option value="all">all statuses</option>
            {statusOptions.map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
          <input
            placeholder="filter GPU…"
            value={gpuFilter}
            onChange={(e) => setGpuFilter(e.target.value)}
          />
          <label
            className="muted"
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <input
              type="checkbox"
              checked={hideExpired}
              onChange={(e) => setHideExpired(e.target.checked)}
            />
            hide expired
          </label>
          <label
            className="muted"
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <input
              type="checkbox"
              checked={inStockOnly}
              onChange={(e) => setInStockOnly(e.target.checked)}
            />
            in stock only
          </label>
        </div>

        {rows.length === 0 ? (
          <p className="muted">
            {signals.length === 0
              ? "No observations yet. Signals appear after the oracle's first poll (RunPod / Kubernetes today; more providers as adapters land) and from live launch outcomes."
              : "No signals match the current filters."}
          </p>
        ) : (
          <div className="table-wrap" style={{ overflowX: "auto" }}>
            <table className="data">
              <thead>
                <tr>
                  <th title="Cloud provider the observation is about">
                    provider
                  </th>
                  <th title="GPU model, normalised so different providers land on the same name">
                    GPU
                  </th>
                  <th title="Provider region / datacenter (zone). Empty = provider-wide observation">
                    region / zone
                  </th>
                  <th title="Sub-tier when the provider has one (e.g. RunPod secure vs community)">
                    type
                  </th>
                  <th title="Is this a good or a bad place to put a replica? Hover a value for what it means">
                    verdict
                  </th>
                  <th title="What the verdict is actually based on">
                    what we know
                  </th>
                  <th title="Should you believe it? MEASURED (we counted it) > PROVIDER (their API says so) > ESTIMATE (their guess) > INFERRED (our own history). STALE = past its TTL">
                    trust
                  </th>
                  <th title="When this was last observed. Past its TTL the row greys out and placement ignores it">
                    age
                  </th>
                  <th title="Where the belief came from (provider API / our own launches / an active probe)">
                    estimated via
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr
                    key={s.id}
                    style={s.expired ? { opacity: 0.45 } : undefined}
                  >
                    <td className="mono">{s.provider}</td>
                    <td className="mono">{s.gpu}</td>
                    <td className="mono">
                      {[s.region, s.zone].filter(Boolean).join(" / ") || "—"}
                    </td>
                    <td className="mono muted">{s.cloud_type || "—"}</td>
                    <td>
                      <Pill
                        tone={VERDICT_TONE[s.verdict ?? "unknown"] ?? "neutral"}
                        title={VERDICT_INFO[s.verdict ?? "unknown"]}
                      >
                        {VERDICT_LABEL[s.verdict ?? "unknown"] ?? s.verdict}
                      </Pill>
                    </td>
                    <td className="muted" style={{ maxWidth: 260 }}>
                      {s.detail || "—"}
                    </td>
                    <td>
                      <Pill
                        tone={TRUST_TONE[s.trust ?? "unknown"] ?? "neutral"}
                        title={s.trust_detail}
                      >
                        {TRUST_LABEL[s.trust ?? "unknown"] ?? s.trust}
                      </Pill>
                    </td>
                    <td className="muted" style={{ whiteSpace: "nowrap" }}>
                      {ageLabel(s.observed_at)}
                      {s.expired ? " · expired" : ""}
                    </td>
                    <td className="muted">{methodLabel(s)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {s.provider === "runpod" && <ProbeButton sig={s} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </>
  );
}

/**
 * Per-row manual probe (spec §7.3): real allocate → confirm → release
 * against this exact (gpu, zone). Server-side it is budget-capped; a
 * 409 means today's probe budget for the provider is spent.
 */
function ProbeButton({ sig }: { sig: CapacitySignal }) {
  const qc = useQueryClient();
  const probe = useMutation({
    mutationFn: () =>
      api.post<ProbeResult>("/capacity/probe", {
        provider: sig.provider,
        gpu: sig.gpu,
        zone: sig.zone,
        cloud_type: sig.cloud_type,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["capacity-signals"] }),
  });

  return (
    <>
      <button
        className="btn btn--ghost"
        onClick={() => probe.mutate()}
        disabled={probe.isPending}
      >
        {probe.isPending ? "Probing…" : "Probe"}
      </button>
      {probe.isSuccess && (
        <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>
          {probe.data.outcome} · ${probe.data.billed_usd.toFixed(3)}
        </span>
      )}
      {probe.isError && (
        <span style={{ marginLeft: 6, fontSize: 11, color: "var(--danger)" }}>
          {probe.error instanceof ApiError
            ? probe.error.message
            : String(probe.error)}
        </span>
      )}
    </>
  );
}
